<template>
  <div
    v-clickaway="close"
    class="relative"
  >
    <slot
      name="search-field"
      v-bind="{doSearch, search, show, selectResult}"
    />

    <div
      v-if="isVisible"
      class="overflow-x-hidden overflow-y-auto absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 rounded dark:divide-gray-800 shadow-md divide scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800 scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
      @click="close"
    >
      <!-- Collection address exact match -->
      <ui-link
        v-if="(only === null || only === 'collection') && isValidAddress && collection"
        class="flex justify-between items-center py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
        @click="selectResult('collection', collection)"
      >
        <collection-list-item
          no-link
          no-overlay
          :pic-size="8"
          :mini="true"
          :collection="collection"
          class="overflow-hidden whitespace-nowrap"
        />
        <slot
          name="result-right"
          type="collection"
        />
      </ui-link>
      <!-- Wallet address exact match -->
      <ui-link
        v-if="(only === null || only === 'wallet') && isValidAddress"
        class="flex justify-between items-center py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
        @click="selectResult('wallet', {id: search.term.toLowerCase()})"
      >
        <wallet-profile
          no-link
          no-overlay
          :wallet="{id: search.term.toLowerCase()}"
        />
        <slot
          name="result-right"
          type="wallet"
        >
          <ui-badge>Wallet</ui-badge>
        </slot>
      </ui-link>
      <!-- Wallet .eth match -->
      <ui-link
        v-if="(only === null || only === 'wallet') && isValidEns && wallet"
        class="flex justify-between items-center py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
        @click="selectResult('wallet', wallet)"
      >
        <wallet-profile
          no-link
          no-overlay
          :wallet="wallet"
        />
        <slot
          name="result-right"
          type="wallet"
        >
          <ui-badge>Wallet</ui-badge>
        </slot>
      </ui-link>
      
      <div
        v-if="isSearchResultsLoading"
        class="p-3"
      >
        <loading-indicator class="text-indigo-600" />
      </div>
      <!-- Search results -->
      <div
        v-if="(only === null || only === 'collection') && sortedResults.length"
        class="max-h-72 dark:divide-gray-700 divide"
      >
        <ui-link
          v-for="item in sortedResults"
          :key="item.id"
          class="flex justify-between items-center py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
          @click="selectResult('collection', item)"
        >
          <collection-list-item
            no-link
            no-overlay
            :pic-size="8"
            :collection="item"
            class="overflow-hidden whitespace-nowrap"
          >
            <template
              #extra-content="{item: resultItem}"
            >
              <span v-if="resultItem.totalVolume">
                Total volume: {{ formatCurrency(resultItem.totalVolume) }}
              </span>
            </template>
          </collection-list-item>
          <slot
            name="result-right"
            type="collection"
          />
        </ui-link>
      </div>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  reactive,
  computed,
  toRefs,
} from "vue";
import { useQuery } from "@vue/apollo-composable";
import searchCollectionsQuery from "@/graphql/collection/queries/searchCollections.query.gql"
import collectionListItemQuery from "@/graphql/collection/queries/collectionListItem.query.gql";
import walletByEnsQuery from "@/graphql/wallet/queries/walletByEns.query.gql"
import { useRecentResults } from "./composition";

import UiLink from "@/components/ui/UiLink";
import web3Utils from 'web3-utils'
import WalletProfile from "@/components/wallets/WalletProfile";
import UiBadge from "@/components/ui/UiBadge";
import CollectionListItem from "@/components/collections/CollectionListItem";
import LoadingIndicator from "@/components/LoadingIndicator";
import { orderBy } from 'lodash-es';
import { formatCurrency } from "@/utils/filters";

export default defineComponent({
  name: "UniversalSearch",

  components: {LoadingIndicator, CollectionListItem, UiBadge, WalletProfile, UiLink},

  props: {
    only: {
      default: null,
      type: String,
    },
  },

  emits: ["select-result"],

  setup(props) {
    const {
      getRecentResultsFromStorage,
      saveRecentResultToStorage,
    } = useRecentResults();

    const { only } = toRefs(props);
    const search = reactive({
      term: "",
    });

    const isValidAddress = computed(() => {
      return search.term && search.term.length > 0 && web3Utils.isAddress(search.term);
    });

    const isValidEns = computed(() => {
      return search.term.slice(-4) === '.eth';
    });

    const {
      result: searchCollectionResult,
      loading: isSearchResultsLoading,
    } = useQuery(
      searchCollectionsQuery,
      () => ({
        search: search.term,
      }),
      () => ({
        enabled: Boolean(search.term.length) && !isValidAddress.value,
      }),
    );
    const searchResults = computed(() => searchCollectionResult.value?.searchCollections);

    const {
      result: collectionResult,
    } = useQuery(
      collectionListItemQuery,
      () => ({ address: search.term }),
      () => ({
        enabled: (only.value === null || only.value === "collection") && isValidAddress.value,
      })
    );
    const collection = computed(() => collectionResult.value?.collection);

    const {
      result: walletByEnsResult,
    } = useQuery(
      walletByEnsQuery,
      () => ({
        address: search.term,
      }),
      () => ({
        enabled: isValidEns.value,
      })
    );
    const wallet = computed(() => walletByEnsResult.value?.wallet);

    return {
      search,
      searchResults,
      isValidEns,
      isValidAddress,
      collection,
      wallet,
      isSearchResultsLoading,
      saveRecentResultToStorage,
      getRecentResultsFromStorage,
      formatCurrency,
    }
  },

  data() {
    return {
      isVisible: false,
    }
  },

  computed: {
    sortedResults() {
      if (!this.search.term) {
        return this.getRecentResultsFromStorage(this.only);
      }

      return orderBy(this.searchResults || [], 'totalVolume', 'desc');
    },
  },

  methods: {
    selectResult(type, data) {
      this.saveRecentResultToStorage({ type, data });
      this.$emit("select-result", {type, data});
      this.search.term = "";
    },

    doSearch(value) {
      const contractAddress = value.toLowerCase();

      if (!web3Utils.isAddress(contractAddress) && this.searchResults?.length > 0) {
        return this.$router.push({name: 'Collections.Show', params: {contractAddress: this.searchResults[0].id}});
      }

      if (!web3Utils.isAddress(contractAddress)) {
        return;
      }

      this.$router.push({name: 'Collections.Show', params: {contractAddress}})
    },

    close() {
      this.isVisible = false;
    },

    show() {
      this.isVisible = true;
    }
  }
});
</script>

<style scoped>

</style>
